// Building protocol Actions (as protobuf JSON for the API) and reading the
// protobuf JSON the server returns, with the generated schemas.

import { create, fromJson, toJson, type JsonValue } from "@bufbuild/protobuf";
import {
  ActionResultSchema,
  ActionSchema,
  ActionStatus,
  CallEventSchema,
  CapabilitiesSchema,
  GlobalActionType,
  NotificationEventSchema,
  SmsEventSchema,
  UiActionType,
  UiTreeSnapshotSchema,
  type Action,
  type ActionResult,
  type CallControl,
  type Capabilities,
  type CallEvent,
  type MediaControl,
  type NotificationEvent,
  type SmsEvent,
  type UiTreeSnapshot,
} from "@fistos/shared";
import type { ProtoJson } from "@fistos/shared/api";

export type ActionKind = Action["kind"];

export function actionJson(kind: ActionKind): ProtoJson {
  return toJson(ActionSchema, create(ActionSchema, { kind })) as ProtoJson;
}

export const actions = {
  global: (action: GlobalActionType) =>
    actionJson({ case: "global", value: { $typeName: "fistos.v1.GlobalAction", action } }),
  launch: (packageName: string) =>
    actionJson({ case: "launchApp", value: { $typeName: "fistos.v1.LaunchApp", packageName } }),
  ui: (nodeId: bigint, action: UiActionType, text?: string) =>
    actionJson({
      case: "ui",
      value: {
        $typeName: "fistos.v1.UiAction",
        nodeId,
        action,
        args:
          text === undefined
            ? []
            : [{ $typeName: "fistos.v1.KeyValue", key: "text", value: { case: "str", value: text } }],
      },
    }),
  tap: (x: number, y: number, long = false) =>
    actionJson({
      case: "input",
      value: {
        $typeName: "fistos.v1.InputAction",
        displayId: 0,
        events: [
          {
            $typeName: "fistos.v1.InputEvent",
            event: { case: "tap", value: { $typeName: "fistos.v1.Tap", x, y, durationMs: long ? 600 : 0 } },
          },
        ],
      },
    }),
  swipe: (x1: number, y1: number, x2: number, y2: number, durationMs = 300) =>
    actionJson({
      case: "input",
      value: {
        $typeName: "fistos.v1.InputAction",
        displayId: 0,
        events: [
          {
            $typeName: "fistos.v1.InputEvent",
            event: { case: "swipe", value: { $typeName: "fistos.v1.Swipe", x1, y1, x2, y2, durationMs } },
          },
        ],
      },
    }),
  typeText: (text: string) =>
    actionJson({
      case: "input",
      value: {
        $typeName: "fistos.v1.InputAction",
        displayId: 0,
        events: [
          {
            $typeName: "fistos.v1.InputEvent",
            event: { case: "text", value: { $typeName: "fistos.v1.TypeText", text } },
          },
        ],
      },
    }),
  call: (op: CallControl["op"], callId = "") =>
    actionJson({ case: "call", value: { $typeName: "fistos.v1.CallControl", callId, op } }),
  sms: (to: string, body: string) => actionJson({ case: "sms", value: { $typeName: "fistos.v1.SmsSend", to, body } }),
  listThreads: (limit = 30) =>
    actionJson({ case: "listSmsThreads", value: { $typeName: "fistos.v1.ListSmsThreads", limit } }),
  query: (what: string) => actionJson({ case: "query", value: { $typeName: "fistos.v1.Query", what, args: [] } }),
  media: (op: MediaControl["op"]) => actionJson({ case: "media", value: { $typeName: "fistos.v1.MediaControl", op } }),
};

export const parse = {
  result: (j: ProtoJson): ActionResult => fromJson(ActionResultSchema, j as JsonValue),
  tree: (j: ProtoJson): UiTreeSnapshot => fromJson(UiTreeSnapshotSchema, j as JsonValue),
  caps: (j: ProtoJson): Capabilities => fromJson(CapabilitiesSchema, j as JsonValue),
  call: (j: ProtoJson): CallEvent => fromJson(CallEventSchema, j as JsonValue),
  sms: (j: ProtoJson): SmsEvent => fromJson(SmsEventSchema, j as JsonValue),
  notification: (j: ProtoJson): NotificationEvent => fromJson(NotificationEventSchema, j as JsonValue),
};

/** ActionStatus name without prefix, e.g. "OK", "REJECTED". */
export function statusName(r: ActionResult): string {
  return ActionStatus[r.status] ?? "UNSPECIFIED";
}

/** Throws with a readable message unless the action succeeded. */
export function expectOk(r: ActionResult): ActionResult {
  if (r.status !== ActionStatus.OK) {
    const name = statusName(r).toLowerCase().replace(/_/g, " ");
    throw new Error(r.message ? `${name}: ${r.message}` : `Action ${name}`);
  }
  return r;
}

export { GlobalActionType, UiActionType };
